import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ProjectFormInitialization, ProjectFormsService } from "@components/project-forms/project-forms.service";
import { UserService } from "@domain/user/user.service";
import { AreaHeaderComponent } from "@odx/angular/components/area-header";
import { FormFieldComponent, FormGroupComponent } from "@odx/angular/components/form-field";
import { map, Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-contact-person-form",
  templateUrl: "./contact-person-form.component.html",
  styleUrls: ["./contact-person-form.component.scss"],
  imports: [UiKitModule, ReactiveFormsModule, AreaHeaderComponent, FormGroupComponent, FormFieldComponent],
  standalone: true,
})
export class ContactPersonFormComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private formBuilder = inject(FormBuilder);
  private projectFormsService = inject(ProjectFormsService);

  @Output() contactPersonFormEmitter$ = new EventEmitter<FormGroup>();
  contactPersonForm!: FormGroup;

  @ViewChild("submitButton")
  submitButton?: ElementRef;

  isReadonly: InputSignal<boolean> = input.required<boolean>();

  protected readonly navigator = navigator;

  private readonly _destroying = new Subject<void>();

  private isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
  isReadOnlyUser = toSignal(this.isReadOnlyUser$);
  isWriteable = signal<boolean>(false);
  constructor() {
    effect(() => {
      this.isReadonly();
      this.isReadOnlyUser();
      const result = !this.isReadOnlyUser() && !this.isReadonly();
      this.isWriteable.update(() => result);
    });

    this.contactPersonForm = this.formBuilder.group({
      firstname: [null, Validators.required],
      lastname: [null, Validators.required],
      department: [],
      email: [
        null,
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/,
          ),
        ],
      ],
      phoneNumber: [null, [Validators.required, Validators.pattern(/^(\+[0-9]+)?([0-9]|\/|\(|\)|-| )+$/)]],
    });

    this.projectFormsService.projectInitialized$.pipe(takeUntil(this._destroying)).subscribe((project) => {
      if (project === null) return;
      return this.initForm(project);
    });

    this.projectFormsService.projectReset$.pipe(takeUntil(this._destroying)).subscribe((project) => {
      this.contactPersonForm.reset();
      this.initForm(project);
    });

    this.contactPersonForm.statusChanges
      .pipe(takeUntil(this._destroying))
      .subscribe(() => this.contactPersonFormEmitter$.emit(this.contactPersonForm));
  }

  ngOnInit(): void {
    this.contactPersonFormEmitter$.emit(this.contactPersonForm);
  }

  ngOnDestroy(): void {
    this._destroying.next();
    this._destroying.complete();
  }

  initForm(project: ProjectFormInitialization) {
    this.contactPersonForm.patchValue({
      firstname: project.contactPerson?.firstname,
      lastname: project.contactPerson?.lastname,
      department: project.contactPerson?.department,
      email: project.contactPerson?.email,
      phoneNumber: project.contactPerson?.phoneNumber,
    });
  }

  triggerSubmit() {
    this.submitButton?.nativeElement.click();
  }
}
